import React from 'react';
import {
  CursorClick,
  Polygon,
  ArrowFatDown,
  ArrowFatUp,
  ArrowBendLeftUp,
  Path,
  Trash,
  LineSegments,
  CircleDashed,
  Minus,
  UploadSimple,
  ArrowCounterClockwise,
} from '@phosphor-icons/react';
import type { ActiveTool } from '../../types/conops';

interface ToolButtonProps {
  tool: ActiveTool;
  activeTool: ActiveTool;
  onSelect: (tool: ActiveTool) => void;
  label: string;
  children: React.ReactNode;
  color?: string;
  activeRing?: string;
}

const ToolButton: React.FC<ToolButtonProps> = ({
  tool,
  activeTool,
  onSelect,
  label,
  children,
  color,
  activeRing,
}) => {
  const isActive = activeTool === tool;
  return (
    <button
      title={label}
      aria-label={label}
      onClick={() => onSelect(tool)}
      className={`
        relative w-10 h-10 flex items-center justify-center rounded-lg transition-all
        ${isActive
          ? `bg-white/10 ring-2 ${activeRing ?? 'ring-[#F26430]'}`
          : 'hover:bg-white/10'
        }
      `}
      style={{ color: color || 'var(--text-primary)' }}
    >
      {children}
    </button>
  );
};

interface ConopsMapToolbarProps {
  activeTool: ActiveTool;
  onToolChange: (tool: ActiveTool) => void;
  onClearAll: () => void;
  onImport: () => void;
  onResetTopology?: () => void;
  topologyImported?: boolean;
  topologySource?: string | null;
  resetLoading?: boolean;
  isDark: boolean;
}

const ConopsMapToolbar: React.FC<ConopsMapToolbarProps> = ({
  activeTool,
  onToolChange,
  onClearAll,
  onImport,
  onResetTopology,
  topologyImported,
  topologySource,
  resetLoading,
  isDark,
}) => {
  const bg = isDark ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-white border-gray-200';
  const sep = `w-7 border-t my-1 ${isDark ? 'border-[#2a2a2a]' : 'border-gray-200'}`;

  return (
    <div
      className={`absolute left-2 top-2 bottom-2 z-10 flex flex-col items-center gap-1 p-1.5 rounded-xl border shadow-lg overflow-y-auto ${bg}`}
      style={{ width: '52px', scrollbarWidth: 'none' }}
    >
      {/* ── Selection / base tools ─────────────────────────────────────────── */}
      <ToolButton tool="none" activeTool={activeTool} onSelect={onToolChange} label="Seleccionar">
        <CursorClick size={18} weight={activeTool === 'none' ? 'fill' : 'regular'} />
      </ToolButton>

      <ToolButton tool="aor" activeTool={activeTool} onSelect={onToolChange} label="Dibujar Zona AoR">
        <Polygon size={18} weight={activeTool === 'aor' ? 'fill' : 'regular'} />
      </ToolButton>

      <ToolButton
        tool="handover_arr" activeTool={activeTool} onSelect={onToolChange}
        label="Punto transferencia llegada (ARR)" color="#22c55e" activeRing="ring-green-500"
      >
        <ArrowFatDown size={18} weight={activeTool === 'handover_arr' ? 'fill' : 'regular'} />
      </ToolButton>

      <ToolButton
        tool="handover_dep" activeTool={activeTool} onSelect={onToolChange}
        label="Punto transferencia salida (DEP)" color="#F26430"
      >
        <ArrowFatUp size={18} weight={activeTool === 'handover_dep' ? 'fill' : 'regular'} />
      </ToolButton>

      <ToolButton
        tool="pushback" activeTool={activeTool} onSelect={onToolChange}
        label="Perfil retroceso" color="#9B59B6" activeRing="ring-purple-500"
      >
        <ArrowBendLeftUp size={18} weight={activeTool === 'pushback' ? 'fill' : 'regular'} />
      </ToolButton>

      <ToolButton
        tool="taxi_route" activeTool={activeTool} onSelect={onToolChange}
        label="Ruta de rodaje" color="#F26430"
      >
        <Path size={18} weight={activeTool === 'taxi_route' ? 'fill' : 'regular'} />
      </ToolButton>

      {/* ── Custom topology taxiway ───────────────────────────────────────── */}
      <ToolButton
        tool="custom_taxiway" activeTool={activeTool} onSelect={onToolChange}
        label="TWY personalizada (AIP)" color="#FFD700" activeRing="ring-yellow-400"
      >
        <Minus size={18} weight={activeTool === 'custom_taxiway' ? 'bold' : 'regular'} />
      </ToolButton>

      {/* ── APTR / Roundabout separator ────────────────────────────────────── */}
      <div className={sep} />

      {/* APTR Azul — solid blue centerline (ACI guidelines) */}
      <ToolButton
        tool="aptr_blue" activeTool={activeTool} onSelect={onToolChange}
        label="APTR — Eje azul" color="#1E88E5" activeRing="ring-blue-500"
      >
        <LineSegments size={18} weight={activeTool === 'aptr_blue' ? 'fill' : 'regular'} />
      </ToolButton>

      {/* APTR Naranja — solid orange centerline (ACI guidelines) */}
      <ToolButton
        tool="aptr_orange" activeTool={activeTool} onSelect={onToolChange}
        label="APTR — Eje naranja" color="#F26430"
      >
        <LineSegments size={18} weight={activeTool === 'aptr_orange' ? 'fill' : 'regular'} />
      </ToolButton>

      {/* Airport Apron Roundabout */}
      <ToolButton
        tool="roundabout" activeTool={activeTool} onSelect={onToolChange}
        label="Roundabout de plataforma" color="#FFD700" activeRing="ring-yellow-400"
      >
        <CircleDashed size={18} weight={activeTool === 'roundabout' ? 'fill' : 'regular'} />
      </ToolButton>

      {/* ── Import topology / Reset / Clear ───────────────────────────────── */}
      <div className={sep} />
      <button
        title={topologyImported
          ? `Topología importada (${topologySource === 'aixm' ? 'AIXM' : topologySource === 'geojson' ? 'GeoJSON' : 'AIP PDF'})`
          : 'Importar topología (AIP PDF / GeoJSON / AIXM)'}
        aria-label="Importar topología"
        onClick={onImport}
        className="w-10 h-10 flex items-center justify-center rounded-lg transition-colors"
        style={{
          color: topologyImported ? '#4ade80' : 'var(--text-secondary)',
          background: topologyImported ? 'rgba(74,222,128,0.1)' : 'transparent',
        }}
      >
        <UploadSimple size={18} weight={topologyImported ? 'fill' : 'regular'} />
      </button>
      {topologyImported && onResetTopology && (
        <button
          title={resetLoading ? 'Reseteando mapa…' : 'Resetear mapa — elimina topología importada y raster'}
          aria-label="Resetear mapa"
          onClick={onResetTopology}
          disabled={resetLoading}
          className="w-10 h-10 flex items-center justify-center rounded-lg transition-colors disabled:opacity-50"
          style={{ color: '#f97316', background: 'rgba(249,115,22,0.1)' }}
        >
          <ArrowCounterClockwise size={18} weight="bold" className={resetLoading ? 'animate-spin' : ''} />
        </button>
      )}
      <button
        title="Limpiar elementos CONOPS"
        aria-label="Limpiar elementos CONOPS"
        onClick={onClearAll}
        className="w-10 h-10 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-500/10 transition-colors"
      >
        <Trash size={18} weight="bold" />
      </button>
    </div>
  );
};

export default ConopsMapToolbar;
